import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Paper, List, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider
} from '@mui/material';

const EntitiesDialog = ({
  open,
  onClose,
  data
}) => {
  // Check if we have valid data to display
  const hasEntities = data && data.entities && data.entities.length > 0;
  const isError = data && data.message && data.message.toLowerCase().includes('error');
  const isLoading = data === null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1 }}>Entity Identification Results</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {data?.message || 'Error identifying entities. Please try again later.'}
          </Alert>
        ) : !hasEntities ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No entities were identified in this requirement.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="entities table">
                <TableHead>
                  <TableRow>
                    <TableCell>Entity</TableCell>
                    <TableCell>Canonical Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Found In</TableCell>
                    <TableCell>Confidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.entities.map((entity) => (
                    <TableRow
                      key={`${entity.canonical_name}-${entity.text}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {entity.text}
                      </TableCell>
                      <TableCell>{entity.canonical_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={entity.type}
                          color={getEntityTypeColor(entity.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {entity.found_in && entity.found_in.map((source) => (
                          <Chip
                            key={source}
                            label={source}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {Math.round(entity.confidence * 100)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                Detailed Entity Analysis
              </Typography>
              <List>
                {data.entities.map((entity) => (
                  <Paper key={`${entity.canonical_name}-${entity.text}`} elevation={1} sx={{ mb: 2, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {entity.canonical_name} ({entity.text})
                    </Typography>
                    <Typography variant="body2" component="p" sx={{ color: 'text.secondary', mb: 2 }}>
                      Type: {entity.type} • Confidence: {Math.round(entity.confidence * 100)}%
                    </Typography>
                    <Typography variant="body2" component="p" sx={{ mb: 2 }}>
                      {entity.reasoning}
                    </Typography>
                  </Paper>
                ))}
              </List>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function to determine chip color based on entity type
const getEntityTypeColor = (type) => {
  const typeMap = {
    'System': 'primary',
    'Component': 'secondary',
    'Signal': 'success',
    'Interface': 'warning',
    'Database': 'info',
    'User': 'error',
    'Data': 'default'
  };

  return typeMap[type] || 'default';
};

EntitiesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object
};

export default EntitiesDialog;